/**
 * Framework-agnostic bounded-reconnect controller.
 *
 * Every Papan client that keeps a long-lived socket open (the host realtime
 * socket, the collab/Yjs socket, the CDN SDK socket, and any future one) needs
 * the SAME reconnect discipline. This module is that discipline in one place so
 * the copies can't drift — the original was inlined per-socket in the #189 fix
 * and one socket (collab/useYjs) never got it, which caused a stuck tab to
 * reconnect ~forever against a dead origin and burn a day of Cloudflare quota.
 *
 * The policy, and why each part exists:
 *
 *   1. Exponential backoff from `baseMs`, doubling each failed attempt up to
 *      `ceilingMs`. A fixed short delay (the old collab behaviour) means a
 *      permanently-failing socket retries at a constant high rate forever;
 *      backoff collapses that to ≈ 1 attempt / ceiling.
 *
 *   2. Optional `jitter` (± fraction of the delay). When many clients drop at
 *      the same instant — an origin blip, a rolling restart — a deterministic
 *      backoff makes them all retry in lockstep and thundering-herd the
 *      endpoint right when it's already degraded. Jitter de-synchronises them.
 *      Defaults to 0 so existing callers that assert exact delays are
 *      unaffected; opt in where herd behaviour matters.
 *
 *   3. Stable-gate. Backoff + attempt count reset ONLY after a connection
 *      proves it can stay up: the caller signals `markConnected()`, and the
 *      reset fires `stableAfterMs` later. A socket that flaps (connect → drop
 *      fast) or that reaches the transport handshake but is then closed by a
 *      server-side cap BEFORE it's usable must NOT reset — otherwise the
 *      backoff resets to base every cycle and you get a tight storm despite
 *      "having backoff". The caller cancels the pending reset via
 *      `onDropped()` on every close.
 *
 *   4. Give-up. After `maxAttempts` consecutive failures-to-stabilise, stop and
 *      call `onGiveUp`. This is the hard cap that turns an unbounded stuck-tab
 *      loop into a bounded one; a reload (fresh controller) re-arms.
 *
 * The controller owns NO socket and makes NO transport assumptions — the caller
 * drives it with four signals (`schedule`, `markConnected`, `onDropped`,
 * `cancel`). That keeps it usable for WebSocket today and e.g. SSE later.
 *
 * Caller responsibilities (deliberate non-goals — #222 review):
 *   - ONLY reconnects driven through `schedule()` are bounded. A caller with a
 *     side reconnect path — e.g. useYjs reconnecting immediately on a 4002
 *     "permission revalidate", or remounting on a 4001 / room-epoch change —
 *     bypasses the backoff/cap by design (those are fast, server-driven, one-
 *     shot re-checks, not dead-endpoint retries). A server that *loops* such a
 *     code would loop unbounded; bound it at the caller if that's a real risk.
 *   - Give-up is surfaced ONLY via `onGiveUp`. There is no other signal — a
 *     consumer that doesn't wire it (SDK `onConnectionError`, a UI status/badge)
 *     ends up silently dead after the cap, looking alive but delivering nothing.
 *     Every caller must route `onGiveUp` to a user-visible terminal state.
 *   - Give-up is TERMINAL by design. Recovery is a fresh controller (a new
 *     instance — i.e. a page reload), NOT an in-place `reset()`: auto-resetting
 *     on refocus/reconnect would let a dead endpoint re-arm and storm again,
 *     defeating the cap. So the web callers surface a reload affordance rather
 *     than auto-recovering. `reset()` exists for tests / an explicit future
 *     "reconnect now" button, but no consumer calls it on its own.
 */

export interface ReconnectOptions {
  /** Initial backoff before the first reconnect attempt. Default 1000ms. */
  baseMs?: number
  /**
   * Backoff never grows past this. Default 30_000ms. Note: with `jitter` set,
   * an individual emitted delay can land up to ±`jitter` around the backoff, so
   * the largest *scheduled* delay is `ceilingMs * (1 + jitter)`, not `ceilingMs`.
   */
  ceilingMs?: number
  /**
   * How long a connection must stay open after `markConnected()` before the
   * backoff + attempt count reset to base. Default 10_000ms.
   */
  stableAfterMs?: number
  /**
   * Consecutive failures-to-stabilise before the controller gives up. Default
   * 60. (At the 30s ceiling that's ~30 min of retries before surrender.)
   */
  maxAttempts?: number
  /**
   * Fractional ± randomisation applied to each delay, 0..1. e.g. 0.2 → each
   * delay is uniformly picked from 80%..120% of the nominal backoff. Default 0
   * (deterministic). Use a non-zero value on sockets where a synchronised herd
   * is plausible.
   */
  jitter?: number
  /** Called once when the attempt cap is reached. `attempts` is the final count. */
  onGiveUp?: (attempts: number) => void
  /** Optional structured log sink for scheduling/give-up lines. */
  log?: (message: string) => void
}

export interface ReconnectController {
  /**
   * Arm a backoff timer that invokes `reconnect` after the current delay, then
   * grow the backoff for next time. Returns `false` (and arms nothing) once the
   * attempt cap is hit — `onGiveUp` fires instead. Any already-pending
   * reconnect timer is cleared first, so this is single-flight.
   */
  schedule(reconnect: () => void): boolean
  /**
   * Signal a connection was established. Call from the socket's `onopen`, or —
   * if the transport only becomes usable after a post-handshake application ack
   * (e.g. a `connected` frame) — from that ack, so a socket capped/rejected
   * before it's usable never resets the backoff. Arms the stable-timer.
   */
  markConnected(): void
  /**
   * Signal the connection dropped (call from `onclose`, for every close code).
   * Cancels the pending stable-timer so a drop-before-stable keeps the backoff
   * growing instead of resetting.
   */
  onDropped(): void
  /**
   * Clear all pending timers (reconnect + stable) without touching the backoff
   * state. Use on teardown, or when a newer connect attempt supersedes this one.
   */
  cancel(): void
  /** Clear timers AND reset backoff / attempts / give-up state to initial. */
  reset(): void
  /** Current consecutive failure-to-stabilise count (for logging and tests). */
  attempts(): number
  /** True once the attempt cap has been hit and the controller stopped. */
  gaveUp(): boolean
}

function clampFraction(v: number): number {
  if (!(v > 0)) return 0 // handles NaN / negatives / 0
  return v > 1 ? 1 : v
}

export function createReconnectController(options: ReconnectOptions = {}): ReconnectController {
  const baseMs = options.baseMs ?? 1_000
  const ceilingMs = options.ceilingMs ?? 30_000
  const stableAfterMs = options.stableAfterMs ?? 10_000
  const maxAttempts = options.maxAttempts ?? 60
  const jitter = clampFraction(options.jitter ?? 0)
  const { onGiveUp, log } = options

  let backoff = baseMs
  let attempts = 0
  let givenUp = false
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null
  let stableTimer: ReturnType<typeof setTimeout> | null = null

  const clearReconnectTimer = () => {
    if (reconnectTimer) {
      clearTimeout(reconnectTimer)
      reconnectTimer = null
    }
  }
  const clearStableTimer = () => {
    if (stableTimer) {
      clearTimeout(stableTimer)
      stableTimer = null
    }
  }

  /** Apply ± jitter to a nominal delay. No-op when jitter is 0. */
  const jittered = (ms: number): number => {
    if (jitter <= 0) return ms
    const spread = ms * jitter
    const value = ms - spread + Math.random() * 2 * spread
    // Floor at 10% of the nominal delay: with jitter≈1 the low bound is 0, and a
    // ~0ms retry is a tight-loop storm — never retry faster than a tenth of the
    // target backoff. (No effect for the small jitter fractions callers use.)
    return Math.max(Math.round(ms * 0.1), Math.round(value))
  }

  return {
    schedule(reconnect) {
      if (givenUp) return false
      // Single-flight: never leak a prior pending timer.
      clearReconnectTimer()
      if (attempts >= maxAttempts) {
        givenUp = true
        log?.(`giving up after ${attempts} reconnect attempts (could not stabilise)`)
        onGiveUp?.(attempts)
        return false
      }
      attempts += 1
      const delay = jittered(backoff)
      backoff = Math.min(backoff * 2, ceilingMs)
      log?.(`reconnect scheduled (attempt ${attempts}, delay ${delay}ms, next backoff ${backoff}ms)`)
      reconnectTimer = setTimeout(() => {
        reconnectTimer = null
        reconnect()
      }, delay)
      return true
    },

    markConnected() {
      // Re-arm from scratch: a fresh connection restarts the stability clock.
      clearStableTimer()
      stableTimer = setTimeout(() => {
        stableTimer = null
        // A connection that proved stable clears ALL degraded state — including
        // `givenUp`, so a controller that surrendered can recover if the caller
        // opens a socket out-of-band that holds (e.g. useYjs's server-driven
        // 4002/4001 re-checks). The web callers otherwise treat give-up as
        // terminal and recover via reload, so this rarely fires post-give-up.
        backoff = baseMs
        attempts = 0
        givenUp = false
      }, stableAfterMs)
    },

    onDropped() {
      clearStableTimer()
    },

    cancel() {
      clearReconnectTimer()
      clearStableTimer()
    },

    reset() {
      clearReconnectTimer()
      clearStableTimer()
      backoff = baseMs
      attempts = 0
      givenUp = false
    },

    attempts() {
      return attempts
    },

    gaveUp() {
      return givenUp
    },
  }
}

// ─── Connect (open) timeout ───────────────────────────────────────────────────
//
// The controller above is driven by close/error signals — so a socket that NEVER
// settles gets no retry at all. That is a real failure mode, not a theoretical
// one: a Cloudflare edge stall leaves a WebSocket in CONNECTING indefinitely (no
// open, no error, no close; the upgrade never reaches nginx), and every consumer
// sat on "Connecting…" forever (collab editors, the host realtime socket that
// `waitForRealtimeConnected` gates on).
//
// `armConnectTimeout` bounds the handshake: if the socket has not left CONNECTING
// within `timeoutMs`, it is `close()`d. Closing a CONNECTING WebSocket "fails the
// connection" (WHATWG WebSockets): the browser dispatches `error` then `close`
// (code 1006) asynchronously, so the caller's EXISTING `onclose` → `schedule()`
// path does the retry — same backoff, same attempt cap, one close event → one
// schedule (no second reconnect path to double-schedule or drift). A timed-out
// attempt never reached `markConnected()`, so it counts toward the cap.
//
// Caller contract: call the returned `clear()` from `onopen` (so it can never fire
// after a successful open), from `onclose`, and on every teardown/intentional
// disconnect (unmount, supersede, dev-offline) so no timer outlives the socket.
// Defence in depth: the timer re-checks `readyState === CONNECTING` before closing,
// so a stale fire against an opened/closing socket is a no-op.

/**
 * Default handshake bound. A healthy WS upgrade (TLS + 101 through the CF edge)
 * completes in well under 2s; 10s is >5× headroom for slow/high-latency mobile
 * links while still bounding an edge stall to seconds. It deliberately equals
 * `stableAfterMs`'s default: the same "a connection gets 10s to prove itself"
 * window on both sides of `onopen`.
 */
export const DEFAULT_CONNECT_TIMEOUT_MS = 10_000

/** WebSocket.CONNECTING — a literal so this module needs no DOM lib / global. */
const WS_CONNECTING = 0

/** The minimal socket shape the timeout needs (satisfied by the DOM WebSocket). */
export interface ConnectTimeoutSocket {
  readonly readyState: number
  close(): void
}

export interface ConnectTimeoutOptions {
  /** Default {@link DEFAULT_CONNECT_TIMEOUT_MS}. */
  timeoutMs?: number
  /** Optional structured log sink (one line when the timeout fires). */
  log?: (message: string) => void
}

/**
 * Arm a handshake timeout on a freshly-created socket. Returns an idempotent
 * `clear()` — see the caller contract above.
 */
export function armConnectTimeout(
  ws: ConnectTimeoutSocket,
  options: ConnectTimeoutOptions = {},
): () => void {
  const timeoutMs = options.timeoutMs ?? DEFAULT_CONNECT_TIMEOUT_MS
  let timer: ReturnType<typeof setTimeout> | null = setTimeout(() => {
    timer = null
    if (ws.readyState !== WS_CONNECTING) return
    options.log?.(`connect timeout — socket not open after ${timeoutMs}ms; closing to retry`)
    ws.close()
  }, timeoutMs)
  return () => {
    if (timer) {
      clearTimeout(timer)
      timer = null
    }
  }
}
